// src/utils/clipboard.js
// Utility to copy text to clipboard with graceful fallback for browsers
// that do not fully support the asynchronous Clipboard API (e.g., Safari).

/**
 * Copy arbitrary text to the user's clipboard.
 * 
 * This function first attempts to use the modern asynchronous
 * `navigator.clipboard.writeText` API, which requires a secure context
 * (HTTPS or localhost) and a user gesture. If that fails – either because
 * the API is unavailable or the browser denies the request (common in
 * Safari, especially on iOS) – we fall back to the older synchronous
 * `document.execCommand('copy')` approach.
 *
 * @param {string} text - The text to copy to the clipboard.
 * @returns {Promise<boolean>} - Resolves to `true` if the text was copied
 * successfully, otherwise `false`.
 */
export async function copyText(text) {
  // First, try the modern async Clipboard API.
  if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      // Intentionally swallow the error so we can attempt the fallback.
      // Safari often throws NotAllowedError here in non-secure contexts.
    }
  }

  // Fallback: create a temporary textarea, select its contents, and copy.
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    // Prevent scrolling to bottom on iOS.
    textarea.style.position = 'fixed';
    textarea.style.top = '0';
    textarea.style.left = '0';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    const successful = document.execCommand('copy');
    document.body.removeChild(textarea);
    return successful;
  } catch (err) {
    console.error('Fallback copy failed', err);
    return false;
  }
}
